import React from 'react';
import { Link } from 'react-router-dom';

const Disclaimer = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-[#003366] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold">Disclaimer</h1>
          <p className="text-white/70 mt-2">Last Updated: December 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8">
            <p className="text-amber-800 font-medium">
              Please read this disclaimer carefully before using our services or relying on information provided through our assessments.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Assessment Disclaimer</h2>
          <p className="text-gray-700">
            The Whole-Home Assessment provided by Valenrock™ Remodelers, LLC, doing business as Valenrock™ Home Services, is an advisory service based on visual inspection and professional experience. It is important to understand the following:
          </p>

          <h3 className="text-xl font-semibold text-[#003366] mt-6 mb-3">Not a Home Inspection</h3>
          <p className="text-gray-700">
            Our Whole-Home Assessment is <strong>not a substitute for a formal home inspection</strong> as defined by New Jersey regulations. If you are buying or selling a property, we recommend obtaining a licensed home inspection.
          </p>

          <h3 className="text-xl font-semibold text-[#003366] mt-6 mb-3">Limitations of Visual Assessment</h3>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Our assessments are based on <strong>visual observation only</strong> and do not include invasive testing, opening walls, or destructive analysis</li>
            <li>Hidden defects, issues behind walls, under floors, or in inaccessible areas may not be identified</li>
            <li>We do not test for environmental hazards such as mold, asbestos, radon, or lead paint unless specifically contracted</li>
            <li>Electrical, plumbing, and HVAC assessments are observational and do not include code compliance verification</li>
          </ul>

          <h3 className="text-xl font-semibold text-[#003366] mt-6 mb-3">No Guarantee</h3>
          <p className="text-gray-700">
            While we strive for accuracy, <strong>we do not guarantee</strong> that our assessment will identify all existing or potential issues with your property. The assessment represents our professional opinion at the time of the visit based on visible conditions.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Home Profile Disclaimer</h2>
          <p className="text-gray-700">
            Your Personalized Home Profile is a planning tool, not a warranty or guarantee. Recommendations and timelines are estimates based on general best practices and may vary based on:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Actual usage and wear patterns</li>
            <li>Weather and environmental conditions</li>
            <li>Quality of original installation</li>
            <li>Unforeseen circumstances</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Professional Advice</h2>
          <p className="text-gray-700">
            Information provided through our services is for general guidance only. For specific concerns, we recommend consulting with:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Licensed structural engineers for foundation or structural concerns</li>
            <li>Licensed electricians for electrical system issues</li>
            <li>Licensed plumbers for plumbing concerns</li>
            <li>Licensed HVAC technicians for heating and cooling systems</li>
            <li>Environmental specialists for mold, asbestos, radon, or lead concerns</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Limitation of Liability</h2>
          <p className="text-gray-700">
            To the maximum extent permitted by law:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Valenrock™ Remodelers, LLC is <strong>not liable for any damages, losses, or costs</strong> arising from issues not identified in our assessment</li>
            <li>Our liability is limited to the fee paid for the assessment service</li>
            <li>We are not responsible for decisions made based on our recommendations</li>
            <li>We are not liable for work performed by third-party contractors, even if recommended by us</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Licensing Information</h2>
          <p className="text-gray-700">
            Valenrock™ Remodelers, LLC is a licensed home improvement contractor in the State of New Jersey and operates under the trade name Valenrock™ Home Services.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <p className="text-gray-700"><strong>Legal Entity:</strong> Valenrock™ Remodelers, LLC</p>
            <p className="text-gray-700"><strong>DBA:</strong> Valenrock™ Home Services</p>
            <p className="text-gray-700"><strong>NJ HIC License:</strong> #13VH13590600</p>
            <p className="text-gray-700"><strong>Status:</strong> Fully Licensed & Insured</p>
          </div>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Website Disclaimer</h2>
          <p className="text-gray-700">
            The information on this website is provided for general informational purposes only. While we strive to keep information accurate and up-to-date, we make no representations or warranties about the completeness, accuracy, or reliability of any information on this site.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Third-Party Services</h2>
          <p className="text-gray-700">
            When we recommend or coordinate third-party contractors or vendors, we do so based on our professional experience. However, we do not guarantee their work, and any agreements you enter into with third parties are solely between you and that party.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Data Protection & Confidentiality</h2>
          <p className="text-gray-700 mb-4">
            We are committed to protecting the confidentiality of your Home Profile and assessment data:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>No Data Sales:</strong> We never sell, rent, or trade your personal or property information to third parties</li>
            <li><strong>Confidential Reports:</strong> Your Home Profile and assessment reports are treated as confidential documents</li>
            <li><strong>Limited Access:</strong> Only authorized personnel directly involved in your service can access your data</li>
            <li><strong>Secure Storage:</strong> All client data is stored in encrypted, secure cloud databases</li>
            <li><strong>Data Deletion:</strong> You may request complete deletion of your data at any time by contacting us</li>
          </ul>
          <p className="text-gray-700 mt-4">
            While we implement industry-standard security measures, we cannot guarantee absolute protection against all potential security threats. In the unlikely event of a data breach, we will notify affected clients within 72 hours and take immediate remedial action.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Acknowledgment</h2>
          <p className="text-gray-700">
            By using our services, you acknowledge that you have read and understood this disclaimer and agree to its terms. If you have any questions, please contact us before scheduling an assessment.
          </p>

          <div className="bg-gray-50 p-4 rounded-lg mt-8">
            <p className="text-gray-700"><strong>Valenrock™ Remodelers, LLC d/b/a Valenrock™ Home Services</strong></p>
            <p className="text-gray-700">Email: contact@valenrockhome.services</p>
            <p className="text-gray-700">Phone: (551) 379-9774</p>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link to="/" className="text-[#003366] hover:text-[#CFB973] font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;
